import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ForecastPage() {
  const [scenario, setScenario] = useState("realistic");
  const [w13, setW13] = useState(null);
  const [m12, setM12] = useState(null);

  useEffect(() => { api.get(`/forecast/13-weeks?scenario=${scenario}`).then(r => setW13(r.data)); }, [scenario]);
  useEffect(() => { api.get("/forecast/12-months").then(r => setM12(r.data)); }, []);

  return (
    <div className="space-y-6" data-testid="forecast-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Forecast</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Cashflow-Prognose</h1>
        <p className="text-sm text-neutral-500 mt-2">13 Wochen rollend + 12 Monate strategisch. Retainer als wiederkehrender Umsatz automatisch berücksichtigt.</p>
      </div>

      <Tabs defaultValue="w13">
        <TabsList>
          <TabsTrigger value="w13" data-testid="tab-w13">13 Wochen</TabsTrigger>
          <TabsTrigger value="m12" data-testid="tab-m12">12 Monate</TabsTrigger>
        </TabsList>

        <TabsContent value="w13" className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-500">Szenario:</span>
            <Select value={scenario} onValueChange={setScenario}>
              <SelectTrigger className="w-64" data-testid="scenario-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="best">Best Case (alles kommt pünktlich)</SelectItem>
                <SelectItem value="realistic">Realistic (nur sicher + wahrscheinlich)</SelectItem>
                <SelectItem value="worst">Worst Case (nur sichere Einnahmen)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!w13 ? <div className="text-neutral-500">Lade…</div> : (
            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
              <table className="w-full text-sm" data-testid="w13-table">
                <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="text-left px-3 py-2">KW</th>
                    <th className="text-right px-3 py-2">Anfang</th>
                    <th className="text-right px-3 py-2">+ Kunden</th>
                    <th className="text-right px-3 py-2">+ Retainer</th>
                    <th className="text-right px-3 py-2">− Lieferanten</th>
                    <th className="text-right px-3 py-2">− Media</th>
                    <th className="text-right px-3 py-2">− Fixkosten</th>
                    <th className="text-right px-3 py-2">Ende</th>
                    <th className="text-right px-3 py-2">Frei</th>
                    <th className="text-center px-3 py-2">Ampel</th>
                  </tr>
                </thead>
                <tbody>
                  {w13.weeks.map((w, i) => (
                    <tr key={i} className="border-t border-neutral-100" data-testid={`week-row-${i}`}>
                      <td className="px-3 py-2 font-medium">KW {w.week_number}<div className="text-[10px] text-neutral-500">{w.start_date.slice(5)}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(w.start_balance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{w.cash_in_customers ? "+" + fmtCurrency(w.cash_in_customers) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{w.cash_in_retainers ? "+" + fmtCurrency(w.cash_in_retainers) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-600">{w.cash_out_suppliers ? "-" + fmtCurrency(w.cash_out_suppliers) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-600">{w.cash_out_media ? "-" + fmtCurrency(w.cash_out_media) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-600">{w.cash_out_fixed ? "-" + fmtCurrency(w.cash_out_fixed) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(w.end_balance)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${w.free_money < 0 ? "text-red-600" : ""}`}>{fmtCurrency(w.free_money)}</td>
                      <td className="px-3 py-2 text-center"><span className="dot inline-block" style={{ backgroundColor: w.traffic_light === "red" ? "#EF4444" : w.traffic_light === "yellow" ? "#F59E0B" : "#10B981" }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="m12">
          {!m12 ? <div className="text-neutral-500">Lade…</div> : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Retainer-MRR" value={fmtCurrency(m12.monthly_retainer_revenue)} />
                <Stat label="Hist. Ø Monatsumsatz" value={fmtCurrency(m12.avg_historical_monthly_revenue)} />
                <Stat label="Kum. Gewinn 12M" value={fmtCurrency(m12.cumulative_pre_tax_profit)} />
                <Stat label="Reichweite Ende 12M" value={m12.projected_runway_months ? `${m12.projected_runway_months} Monate` : "—"} />
              </div>
              <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="m12-table">
                  <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="text-left px-3 py-2">Monat</th>
                      <th className="text-right px-3 py-2">Umsatz</th>
                      <th className="text-right px-3 py-2">davon Retainer</th>
                      <th className="text-right px-3 py-2">Wareneinsatz</th>
                      <th className="text-right px-3 py-2">Rohertrag</th>
                      <th className="text-right px-3 py-2">Fixkosten</th>
                      <th className="text-right px-3 py-2">Gewinn v. St.</th>
                      <th className="text-right px-3 py-2">Cashflow</th>
                      <th className="text-right px-3 py-2">End-Kontostand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m12.months.map((m, i) => (
                      <tr key={i} className="border-t border-neutral-100">
                        <td className="px-3 py-2 font-medium">{m.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.revenue_net)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-neutral-500">{fmtCurrency(m.retainer_revenue)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-600">{fmtCurrency(m.cogs)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.gross_profit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-600">{fmtCurrency(m.fixed_costs)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${m.pre_tax_profit < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtCurrency(m.pre_tax_profit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.cashflow)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(m.ending_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
